import React, { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import {
  CalendarDays,
  Check,
  ChevronDown,
  CircleDollarSign,
  ListFilterPlus,
  X,
} from "lucide-react";

import bank from "../../assets/img/bank-i.svg";
import upi from "../../assets/img/upi-i.svg";
import paypal from "../../assets/img/paypal-i.svg";

const DATE_OPTIONS = ["24 Hours", "7 Days", "28 Days", "Custom"];

const PAYMENT_OPTIONS = [
  { label: "All Methods", icon: null },
  { label: "Bank Account", icon: bank },
  { label: "UPI ID", icon: upi },
  { label: "PayPal", icon: paypal },
];

export default function TransactionHistoryFilter({
  isOpen,
  dateFilter,
  onDateFilterChange,
  onClose,
}) {
  const [dateRange, setDateRange] = useState([null, null]);
  const [datePosted, setDatePosted] = useState("");
  const [paymentMethod, setPaymentMethod] = useState("All Methods");
  const [paymentMenuOpen, setPaymentMenuOpen] = useState(false);
  const datePickerRef = useRef(null);

  const [startDate, endDate] = dateRange;
  const activeFilterCount = Number(Boolean(datePosted)) + Number(paymentMethod !== "All Methods");

  useEffect(() => {
    setDatePosted(dateFilter?.datePosted ?? "");
    setDateRange(dateFilter?.dateRange ?? [null, null]);
  }, [dateFilter]);

  useEffect(() => {
    if (!isOpen) {
      setPaymentMenuOpen(false);
      return undefined;
    }

    const handleEscape = (event) => {
      if (event.key === "Escape") onClose();
    };

    document.addEventListener("keydown", handleEscape);

    return () => {
      document.removeEventListener("keydown", handleEscape);
    };
  }, [isOpen, onClose]);

  const handleDateChange = (value) => {
    setDatePosted(value);
    if (value !== "Custom") setDateRange([null, null]);
  };

  const handleReset = () => {
    setDatePosted("");
    setDateRange([null, null]);
    setPaymentMethod("All Methods");
    setPaymentMenuOpen(false);
  };

  const handleApplyFilters = () => {
    onDateFilterChange?.({
      datePosted,
      dateRange: datePosted === "Custom" ? dateRange : [null, null],
      paymentMethod,
    });
  };

  return createPortal(
    <>
      <aside
        id="transaction-filter-drawer"
        className={`transaction-filter-drawer ${isOpen ? "is-open" : ""}`}
        role="dialog"
        aria-hidden={!isOpen}
        aria-labelledby="transaction-filter-title"
      >
        <header className="transaction-filter-header">
          <div className="transaction-filter-heading-icon" aria-hidden="true">
            <ListFilterPlus size={21} />
          </div>
          <div>
            <div className="transaction-filter-eyebrow">Refine results</div>
            <h2 id="transaction-filter-title">Filter Transactions</h2>
            <p>
              {activeFilterCount
                ? `${activeFilterCount} filter${activeFilterCount > 1 ? "s" : ""} selected`
                : "Choose filters to narrow your activity"}
            </p>
          </div>
          <button
            type="button"
            className="transaction-filter-close"
            aria-label="Close transaction filters"
            onClick={onClose}
          >
            <X size={19} />
          </button>
        </header>

        <div className="transaction-filter-body">
          <section className="transaction-filter-group" aria-labelledby="date-posted-title">
            <div className="transaction-filter-group-title">
              <span><CalendarDays size={17} /></span>
              <div>
                <h3 id="date-posted-title">Date Range</h3>
                <p>Select a transaction period</p>
              </div>
            </div>

            <div className="transaction-date-options">
              {DATE_OPTIONS.map((option) => {
                const optionId = `transaction-date-${option.toLowerCase().replace(/\s+/g, "-")}`;
                return (
                  <label
                    className={`transaction-date-option ${datePosted === option ? "is-selected" : ""}`}
                    htmlFor={optionId}
                    key={option}
                  >
                    <input
                      id={optionId}
                      type="radio"
                      name="datePosted"
                      value={option}
                      checked={datePosted === option}
                      onChange={() => handleDateChange(option)}
                    />
                    <span className="transaction-radio" aria-hidden="true" />
                    <span>{option}</span>
                  </label>
                );
              })}
            </div>

            <div className={`transaction-custom-date ${datePosted === "Custom" ? "is-visible" : ""}`}>
              {datePosted === "Custom" && (
                <div className="transaction-date-input-wrap">
                  <DatePicker
                    ref={datePickerRef}
                    selectsRange
                    startDate={startDate}
                    endDate={endDate}
                    onChange={setDateRange}
                    placeholderText="Select date range"
                    dateFormat="dd MMM yyyy"
                    portalId="transaction-datepicker-portal"
                    popperClassName="transaction-date-popper"
                    calendarClassName="transaction-range-calendar"
                    popperPlacement="bottom-end"
                    showPopperArrow={false}
                    shouldCloseOnSelect={false}
                    className="transaction-date-input"
                  />
                  <button
                    type="button"
                    className="transaction-calendar-button"
                    aria-label="Open date range calendar"
                    onClick={() => datePickerRef.current?.setOpen(true)}
                  >
                    <CalendarDays size={17} aria-hidden="true" />
                  </button>
                </div>
              )}
            </div>
          </section>

          <section className="transaction-filter-group" aria-labelledby="payment-method-title">
            <div className="transaction-filter-group-title">
              <span><CircleDollarSign size={18} /></span>
              <div>
                <h3 id="payment-method-title">Payment Method</h3>
                <p>For spent and redeemed transactions</p>
              </div>
            </div>

            <div className="transaction-payment-select">
              <button
                type="button"
                className={`transaction-payment-trigger ${paymentMenuOpen ? "is-open" : ""}`}
                aria-haspopup="listbox"
                aria-expanded={paymentMenuOpen}
                onClick={() => setPaymentMenuOpen((open) => !open)}
              >
                <span>{paymentMethod}</span>
                <ChevronDown size={19} />
              </button>

              {paymentMenuOpen && (
                <div className="transaction-payment-menu" role="listbox" aria-label="Payment method">
                  {PAYMENT_OPTIONS.map((option) => (
                    <button
                      type="button"
                      role="option"
                      aria-selected={paymentMethod === option.label}
                      className={paymentMethod === option.label ? "is-selected" : ""}
                      key={option.label}
                      onClick={() => {
                        setPaymentMethod(option.label);
                        setPaymentMenuOpen(false);
                      }}
                    >
                      <span className="transaction-payment-icon">
                        {option.icon ? <img src={option.icon} alt="" /> : <CircleDollarSign size={17} />}
                      </span>
                      <span>{option.label}</span>
                      {paymentMethod === option.label && <Check size={17} />}
                    </button>
                  ))}
                </div>
              )}
            </div>
          </section>
        </div>

        <footer className="transaction-filter-actions">
          <button type="button" className="transaction-filter-reset" onClick={handleReset}>
            Reset
          </button>
          <button type="button" className="transaction-filter-apply" onClick={handleApplyFilters}>
            Apply Filters
            {activeFilterCount > 0 && <span>{activeFilterCount}</span>}
          </button>
        </footer>
      </aside>
    </>,
    document.body
  );
}
